import { type SearchResp } from "./api";
import { OBSERVATION_POLICY } from "./observation";

export interface SearchMatch { kind: "tx" | "account" | "round"; id: string }
export interface SearchState { busy: boolean; matches: SearchMatch[]; message: string | null }
export const emptySearch = (): SearchState => ({ busy: false, matches: [], message: null });

export function searchMatches(response: SearchResp): SearchMatch[] {
  const groups = [
    { kind: "tx", ids: response.txs.map((t) => t.tx_id) },
    { kind: "account", ids: response.accounts.flatMap((a) => a ? [a.account_id] : []) },
    { kind: "round", ids: response.rounds.flatMap((r) => r.round == null ? [] : [String(r.round)]) },
  ] as const;
  return [...new Map(groups.flatMap((g) => g.ids.map((id) => [
    `${g.kind}:${id}`, { kind: g.kind, id },
  ] as const))).values()];
}

export function exactSearch(query: string): SearchMatch | null {
  const patterns = [
    ["tx", /^[0-9a-f]{64}$/i], ["account", /^[0-9a-f]{40}$/i], ["round", /^\d+$/],
  ] as const;
  const found = patterns.find(([, pattern]) => pattern.test(query));
  return found ? { kind: found[0], id: query.toLowerCase() } : null;
}

// A bounded one-shot request, not a poll. Cancellation also invalidates work
// that already completed or whose transport ignores AbortSignal.
export function createSearch(
  read: (query: string, signal: AbortSignal) => Promise<SearchResp>,
  publish: (state: SearchState) => void,
  select: (match: SearchMatch) => void,
  timeoutMs = OBSERVATION_POLICY.timeoutMs,
) {
  const run = {
    generation: 0, controller: null as AbortController | null,
    deadline: undefined as ReturnType<typeof setTimeout> | undefined,
  };
  const cancel = () => {
    ++run.generation;
    clearTimeout(run.deadline);
    run.controller?.abort();
  };
  const submit = (value: string) => {
    cancel();
    const query = value.trim();
    publish(emptySearch());
    if (!query) return;
    const exact = exactSearch(query);
    if (exact) { select(exact); return; }
    const current = run.generation;
    const controller = new AbortController();
    run.controller = controller;
    publish({ ...emptySearch(), busy: true });
    const finish = (found: SearchMatch[] | null, error: string | null) => {
      if (current !== run.generation) return;
      cancel();
      const matches = found ?? [];
      publish({ busy: false, matches, message: error ?? (matches.length ? null : "No matching records.") });
      if (matches.length === 1) select(matches[0]!);
    };
    run.deadline = setTimeout(() => finish(null, "Search request timed out. Try again."), timeoutMs);
    Promise.resolve().then(() => current === run.generation ? read(query, controller.signal) : null)
      .then((response) => response ? searchMatches(response) : null).then(
        (matches) => { if (matches) finish(matches, null); },
        () => finish(null, "Search unavailable. Try again."),
      );
  };
  return { submit, cancel };
}
